import isNil from 'lodash/isNil';
import sortBy from 'lodash/sortBy';
import moment from 'moment';
import React from 'react';
import { useMutation, useQuery } from 'react-query';
import { toastr } from 'react-redux-toastr';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import { get, post } from '../../utils/api';
import { withStatusSink } from '../../utils/requestStatusSink';
import {
  SCREENING_POLL_INTERVAL,
  SCREENING_STATUSES,
  SUBMITTED_SCREENING_STATUSES,
} from './constants';
import messages from './messages';
import { SCREENING_METHOD_STATUS } from './ProviderScreening/constants';
import type { UseScreeningAccordionReturnValue } from './types';
import {
  getProviderProfilesFromHouseholdScreening,
  mapProviderPropertyProfileToScreeningProvider,
  getScreeningApplicationErrors,
  isPhasedTransUnionProfile,
  splitPhasedProvider,
} from './screeningProviderUtils';

export function getHouseholdScreeningRefetchInterval(
  queryData,
  { shouldPoll, isResident },
) {
  // If shouldPoll is explicitly provided (legacy callers), honour it.
  if (typeof shouldPoll !== 'undefined') {
    return shouldPoll ? SCREENING_POLL_INTERVAL : false;
  }
  const result = queryData?.result;
  // Always poll while the screening is transitioning from a new submission.
  if (result?.status === 'NOT_STARTED') return SCREENING_POLL_INTERVAL;
  // A phased screening held at phase 1 cannot change until a user requests
  // phase 2, unless a request is still running, a started request has no
  // result yet, or a phase 1 detail is still pending. A later provider stays
  // NOT_STARTED through the hold, because providers run in order. A COMPLETED
  // screening at phase 1 (an automatic decline) falls through to the COMPLETED
  // branch, because the final decision row lands after the status.
  if (result?.status === 'IN_PROGRESS' && result?.phase === 1) {
    const hasPendingPhaseOneWork = result?.requests?.some(
      (request) =>
        request?.status === 'IN_PROGRESS' ||
        (!request?.result && request?.status !== 'NOT_STARTED') ||
        request?.result?.details?.some(
          (detail) => detail?.status === 'PENDING',
        ),
    );
    if (!hasPendingPhaseOneWork) return false;
  }
  if (result?.status === 'COMPLETED') {
    // For this household-screening resident flow, stop polling once the
    // top-level screening reaches COMPLETED instead of waiting for finalDecisions.
    if (isResident) return false;
    // For non-resident screenings there is a brief race window between the
    // backend setting status=COMPLETED and creating the ScreeningFinalDecision.
    // Keep polling until the decision appears so the UI updates automatically
    // without requiring a manual refresh.
    if (!result?.finalDecisions?.length) return SCREENING_POLL_INTERVAL;
    return false;
  }
  if (result?.status !== 'IN_PROGRESS') return false;
  // For residents: the top-level status stays IN_PROGRESS even after all
  // results resolve, so we stop polling once there are no pending requests.
  if (isResident) {
    const hasPendingRequest = result?.requests?.some(
      (r) => !r.result || r.result.status === 'PENDING',
    );
    return hasPendingRequest ? SCREENING_POLL_INTERVAL : false;
  }
  // For non-residents: keep polling until the backend transitions the
  // top-level status to COMPLETED (happens asynchronously after all results
  // arrive). Stopping early leaves the status stuck at PENDING in the UI.
  return SCREENING_POLL_INTERVAL;
}

export function useHouseholdScreening({
  householdId,
  shouldPoll,
  isResident = false,
  enabled = true,
}) {
  const { data, isLoading, refetch } = useQuery(
    ['householdScreening', householdId],
    () => {
      return get(`/screening-service/screenings/households/${householdId}`);
    },
    {
      refetchInterval: (queryData) =>
        getHouseholdScreeningRefetchInterval(queryData, {
          shouldPoll,
          isResident,
        }),
      refetchOnWindowFocus: false,
      enabled,
    },
  );

  return {
    householdScreening: data?.result,
    isLoadingHouseholdScreening: isLoading,
    refetchHouseholdScreening: refetch,
  };
}

export function useValidateApplicants({
  applicationId,
  enabled = true,
  isResident,
}) {
  const { data } = useQuery(
    ['validateApplicants', applicationId],
    () => {
      if (isResident) {
        return null;
      }

      return get(
        `/screening-service/screenings/applicants/validate/${applicationId}`,
      );
    },
    {
      refetchOnWindowFocus: false,
      enabled: enabled && !isResident,
    },
  );

  if (data?.parsedErrors?.missingOrInvalidFields) {
    data.parsedErrors.missingOrInvalidFields =
      data.parsedErrors.missingOrInvalidFields.map((field) => {
        if (field.includes('Dob')) {
          return 'Date of Birth';
        }
        return field;
      });
  }

  return {
    applicantsValidationResult: data,
  };
}

export function useOneUser({ userId }) {
  const selectedProperty = useSelectedProperty();
  const { data } = useQuery(
    ['user', userId, selectedProperty.organizationId],
    () => {
      return get(`/${selectedProperty.organizationId}/users/${userId}`);
    },
    {
      enabled: !isNil(userId),
      refetchOnWindowFocus: false,
    },
  );

  return data;
}

export function useScreeningAccordion(): UseScreeningAccordionReturnValue {
  const [expanded, setExpanded] = React.useState(false);
  const toggle = () => setExpanded(!expanded);

  return {
    expanded,
    toggle,
  };
}

export function useScreeningPollFlag(initialValue: boolean = true) {
  const [shouldPoll, setShouldPoll] = React.useState(initialValue);

  const startPolling = React.useCallback(() => setShouldPoll(true), []);
  const stopPolling = React.useCallback(() => setShouldPoll(false), []);

  return {
    shouldPoll,
    startPolling,
    stopPolling,
  };
}

export function useScreeningProviders({
  householdScreening,
  activeProviderPropertyProfiles,
  applicantsValidationResult,
  intl,
  applicationStatus,
  isResident,
}) {
  return React.useMemo(() => {
    const screeningProviderProfiles = getProviderProfilesFromHouseholdScreening(
      {
        intl,
        householdScreening,
      },
    );
    const providerProfiles =
      screeningProviderProfiles ?? activeProviderPropertyProfiles;
    const applicantValidationErrors = applicantsValidationResult?.parsedErrors;
    const screeningStatus = householdScreening?.status;

    // A phased profile becomes two entries: the criminal box and the rest.
    const mappedScreeningProviders = providerProfiles.flatMap(
      (providerPropertyProfile) => {
        const mappedProvider = mapProviderPropertyProfileToScreeningProvider({
          providerPropertyProfile,
          screeningStatus,
          applicantValidationErrors,
          intl,
          applicationStatus,
          isResident,
          screeningPhase: householdScreening?.phase,
        });
        return splitPhasedProvider(
          mappedProvider,
          isPhasedTransUnionProfile(
            providerPropertyProfile,
            householdScreening?.phase,
          ),
        );
      },
    );

    return sortBy(mappedScreeningProviders, 'order');
  }, [
    householdScreening,
    activeProviderPropertyProfiles,
    applicantsValidationResult,
    intl,
    applicationStatus,
    isResident,
  ]);
}

export function isPhaseOneHold(householdScreening) {
  if (!householdScreening || householdScreening.phase !== 1) return false;
  if (householdScreening.finalDecisions?.length > 0) return false;
  // The hold keeps the phased request PENDING. A request that declined,
  // errored, or was canceled at phase 1 has nothing to continue.
  return (householdScreening.requests ?? []).some(
    (request) =>
      request?.status === 'PENDING' &&
      request?.providerPropertyProfile?.provider?.name === 'TransUnion' &&
      isPhasedTransUnionProfile(
        request.providerPropertyProfile,
        householdScreening.phase,
      ),
  );
}

export function hasPendingPhaseOneResult(householdScreening) {
  return (householdScreening?.requests ?? []).some(
    (request) =>
      request?.providerPropertyProfile?.provider?.name === 'TransUnion' &&
      isPhasedTransUnionProfile(
        request.providerPropertyProfile,
        householdScreening.phase,
      ) &&
      request.result?.details?.some((detail) => detail?.status === 'PENDING'),
  );
}

export function useActiveProviderPropertyProfiles(providerPropertyProfiles) {
  return React.useMemo(
    () =>
      providerPropertyProfiles?.filter(
        (providerPropertyProfile) => providerPropertyProfile.isActive,
      ),
    [providerPropertyProfiles],
  );
}

export function hasMethodWithStatus(screeningProviders, targetStatus) {
  return screeningProviders.some((provider) =>
    provider.methods.some((method) => method.status === targetStatus),
  );
}

function getHouseholdStatus(householdScreening) {
  if (!householdScreening || householdScreening.status === 'NOT_STARTED') {
    return SCREENING_STATUSES.NOT_STARTED;
  }

  const statusMap = {
    IN_PROGRESS: SCREENING_STATUSES.PENDING,
    CANCELED: SCREENING_STATUSES.CANCELED,
    ERROR: SCREENING_STATUSES.ERROR,
  };

  return statusMap[householdScreening.status] ?? null;
}

function getFinalDecisionStatus(finalDecision) {
  if (!finalDecision?.finalStatus) return null;

  const statusMap = {
    APPROVED: SCREENING_STATUSES.APPROVED,
    DECLINED: SCREENING_STATUSES.DECLINED,
    APPROVED_WITH_CONDITIONS: SCREENING_STATUSES.APPROVED_WITH_CONDITIONS,
  };

  return statusMap[finalDecision.finalStatus] ?? null;
}

export function useApplicationError({ intl, householdScreening }) {
  return React.useMemo(() => {
    const applicants = householdScreening?.payload?.applicants;
    const requests = householdScreening?.requests ?? [];
    // Prefer a request that has a result with error details (notes) so that
    // specific validation messages are shown over the generic fallback.
    // Fall back to any request with an ERROR status when no result exists.
    const screeningErrorRequests =
      requests.find(
        (request) =>
          (request?.status === 'ERROR' ||
            request?.result?.status === 'ERROR') &&
          request?.result?.notes,
      ) ??
      requests.find(
        (request) =>
          request?.status === 'ERROR' || request?.result?.status === 'ERROR',
      );
    if (!screeningErrorRequests) {
      return null;
    }
    return getScreeningApplicationErrors(
      intl,
      screeningErrorRequests,
      applicants,
    );
  }, [intl, householdScreening]);
}

export function useScreeningStatus({
  screeningProviders,
  applicantsValidationResult,
  householdScreening,
  finalDecision,
  isResident,
}) {
  return React.useMemo(() => {
    // Check final decision status
    const decisionStatus = getFinalDecisionStatus(finalDecision);
    if (decisionStatus) {
      return decisionStatus;
    }

    // Check for validation errors or no providers
    const hasMissingOrInvalidFields =
      applicantsValidationResult?.parsedErrors?.missingOrInvalidFields.length >
      0;
    if (
      !isResident &&
      (hasMissingOrInvalidFields || !screeningProviders.length)
    ) {
      return SCREENING_STATUSES.NOT_READY;
    }

    // The criminal tile reads Not Started through the hold, so the hold states
    // its own Pending.
    if (isPhaseOneHold(householdScreening)) {
      return SCREENING_STATUSES.PENDING;
    }

    // For non-resident screenings, check for PENDING and ERROR method statuses.
    // These checks are skipped for residents (see below).
    // Note: hasMethodWithStatus is used (not hasMethodWithFortressStatus) because
    // fortressStatus is not stored on the method object — method.status is correctly
    // derived via mapFortressStatusToBadgeVariant and is reliable for all providers
    // including BetterNOI which returns partial results (some methods PENDING, others
    // already resolved) before the overall screening is complete.
    if (!isResident) {
      if (
        hasMethodWithStatus(screeningProviders, SCREENING_METHOD_STATUS.PENDING)
      ) {
        return SCREENING_STATUSES.PENDING;
      }

      if (
        hasMethodWithStatus(screeningProviders, SCREENING_METHOD_STATUS.ERROR)
      ) {
        return SCREENING_STATUSES.ERROR;
      }
    }

    // For resident screenings, derive status directly from method statuses.
    // Residents never record a Final Decision, and the top-level screening
    // status may remain IN_PROGRESS even when all results are resolved.
    // fortressStatus is also unreliable (not always updated on PATCH), so we
    // bypass those checks entirely for residents.
    if (isResident) {
      if (
        hasMethodWithStatus(screeningProviders, SCREENING_METHOD_STATUS.ERROR)
      ) {
        return SCREENING_STATUSES.ERROR;
      }
      if (
        hasMethodWithStatus(screeningProviders, SCREENING_METHOD_STATUS.PENDING)
      ) {
        return SCREENING_STATUSES.PENDING;
      }
      if (
        hasMethodWithStatus(screeningProviders, SCREENING_METHOD_STATUS.FAIL)
      ) {
        return SCREENING_STATUSES.DECLINED;
      }
      if (
        hasMethodWithStatus(
          screeningProviders,
          SCREENING_METHOD_STATUS.REFER,
        ) ||
        hasMethodWithStatus(
          screeningProviders,
          SCREENING_METHOD_STATUS.ACCEPT_WITH_CONDITIONS,
        )
      ) {
        return SCREENING_STATUSES.APPROVED_WITH_CONDITIONS;
      }
      return SCREENING_STATUSES.APPROVED;
    }

    if (
      hasMethodWithStatus(screeningProviders, SCREENING_METHOD_STATUS.CANCELED)
    ) {
      return SCREENING_STATUSES.INCOMPLETE;
    }

    if (hasMethodWithStatus(screeningProviders, SCREENING_METHOD_STATUS.FAIL)) {
      return SCREENING_STATUSES.INCOMPLETE;
    }

    // Check household status
    const householdStatus = getHouseholdStatus(householdScreening);
    if (householdStatus) {
      return householdStatus;
    }

    return SCREENING_STATUSES.INCOMPLETE;
  }, [
    screeningProviders,
    applicantsValidationResult,
    householdScreening,
    finalDecision,
    isResident,
  ]);
}

export function useFinalDecision({ householdScreening }) {
  const finalDecision = React.useMemo(() => {
    if (!householdScreening || householdScreening?.finalDecisions?.length === 0)
      return null;

    const { finalDecisions } = householdScreening;

    return finalDecisions.sort((a, b) => {
      return new Date(b.createdAt) - new Date(a.createdAt);
    })[0];
  }, [householdScreening]);
  const finalDecisionUser = useOneUser({
    userId: finalDecision?.createdById,
  });

  const finalDecisionUserFullName = finalDecisionUser
    ? `${finalDecisionUser.firstName} ${finalDecisionUser.lastName}`
    : 'Fortress';
  const finalDecisionDate = finalDecision?.createdAt
    ? moment(finalDecision.createdAt).format('YYYY-MM-DD')
    : '';
  const finalDecisionData = finalDecision
    ? {
        ...finalDecision,
        decisionUserFullName: finalDecisionUserFullName,
        decisionDate: finalDecisionDate,
      }
    : null;

  return finalDecisionData;
}

export function useSubmissionData({ householdScreening, screeningStatus }) {
  const submittedByUser = useOneUser({
    userId: householdScreening?.createdById,
  });

  const submittedByUserFullName = submittedByUser
    ? `${submittedByUser.firstName} ${submittedByUser.lastName}`
    : '';
  const submittedOnFormatted = householdScreening?.createdAt
    ? moment(householdScreening.createdAt).format('YYYY-MM-DD')
    : '';
  const submissionData =
    submittedByUser && SUBMITTED_SCREENING_STATUSES.includes(screeningStatus)
      ? {
          submittedBy: submittedByUserFullName,
          submittedOn: submittedOnFormatted,
        }
      : null;

  return submissionData;
}

const OPEN_REQUEST_STATUSES = ['NOT_STARTED', 'PENDING', 'IN_PROGRESS'];

/**
 * A phased screening carries a decided credit and eviction result with no
 * criminal detail twice: during the phase 1 hold, and while phase 2 is in
 * flight after the request. Its TransUnion request stays open both times, so
 * no final decision may be recorded until that request completes. A screening
 * that is not phased keeps today's result-status rule.
 */
export function didCompleteAllScreeningMethods(householdScreening) {
  if (!householdScreening?.requests?.length) return false;
  if (
    householdScreening.phase != null &&
    householdScreening.requests.some((r) =>
      OPEN_REQUEST_STATUSES.includes(r?.status),
    )
  ) {
    return false;
  }

  return !householdScreening.requests.some(
    (r) =>
      !r.result ||
      ['PENDING', 'ERROR', 'CANCELED', 'NOT_STARTED'].includes(r.result.status),
  );
}

export function useDidCompleteAllScreeningMethods({ householdScreening }) {
  return React.useMemo(
    () => didCompleteAllScreeningMethods(householdScreening),
    [householdScreening],
  );
}

export function useTooltipText({
  screeningStatus,
  isApplicationCanceledOrDenied,
}) {
  return React.useMemo(() => {
    if (screeningStatus === SCREENING_STATUSES.PENDING) {
      return 'Current screening is still in progress.';
    }

    if (isApplicationCanceledOrDenied) {
      return 'Cannot screen canceled or denied applications.';
    }

    return null;
  }, [screeningStatus, isApplicationCanceledOrDenied]);
}

async function submitPhasedScreeningAction({
  intl,
  path,
  body,
  successMessage,
  conflictMessage,
  failureMessage,
  refetchHouseholdScreening,
}) {
  let failureStatus = null;
  try {
    await withStatusSink(
      (status) => {
        failureStatus = status;
      },
      () => post(path, JSON.stringify(body)),
    );
    toastr.success(
      intl.formatMessage(messages.success),
      intl.formatMessage(successMessage),
    );
    // The modal closes after this function returns. Wait for the fresh
    // screening first, so the buttons are gone when the modal goes.
    await refetchHouseholdScreening();
    return true;
  } catch (error) {
    if (failureStatus === 409) {
      toastr.info(
        intl.formatMessage(messages.info),
        intl.formatMessage(conflictMessage),
      );
      await refetchHouseholdScreening();
      return true;
    }
    toastr.error(
      intl.formatMessage(messages.error),
      intl.formatMessage(failureMessage),
    );
    return false;
  }
}

export function useRequestCriminalReport({
  intl,
  organizationId,
  propertyId,
  applicationId,
  screeningId,
  refetchHouseholdScreening,
  onDone,
}) {
  const mutation = useMutation(() =>
    submitPhasedScreeningAction({
      intl,
      path: `/${organizationId}/${propertyId}/screening-service/screenings/${screeningId}/phase-two`,
      body: { applicationId },
      successMessage: messages.criminalReportRequested,
      conflictMessage: messages.phasedScreeningAlreadyResolved,
      failureMessage: messages.failedToRequestCriminalReport,
      refetchHouseholdScreening,
    }),
  );
  const { mutateAsync } = mutation;

  const requestCriminalReport = React.useCallback(async () => {
    const isDone = await mutateAsync();
    if (isDone) onDone?.();
  }, [mutateAsync, onDone]);

  return { isSubmitting: mutation.isLoading, requestCriminalReport };
}

export function useDeclineWithoutCriminalScreening({
  intl,
  organizationId,
  propertyId,
  applicationId,
  screeningId,
  refetchHouseholdScreening,
  onDone,
}) {
  const mutation = useMutation((reason) =>
    submitPhasedScreeningAction({
      intl,
      path: `/${organizationId}/${propertyId}/screening-service/screenings/${screeningId}/phase-two/decline`,
      body: { applicationId, reason },
      successMessage: messages.applicantDeclinedWithoutCriminalScreening,
      conflictMessage: messages.phasedScreeningAlreadyResolved,
      failureMessage: messages.failedToDeclineWithoutCriminalScreening,
      refetchHouseholdScreening,
    }),
  );
  const { mutateAsync } = mutation;

  const declineWithoutCriminalScreening = React.useCallback(
    async (reason) => {
      const isDone = await mutateAsync(reason);
      if (isDone) onDone?.();
    },
    [mutateAsync, onDone],
  );

  return {
    isSubmitting: mutation.isLoading,
    declineWithoutCriminalScreening,
  };
}
